import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { SubscribeButton } from './SubscribeButton';
import { MessageButton } from './MessageButton';
import FollowersModal from './FollowersModal';
import { ClickableProfilePhoto } from './PhotoLightbox';
import {
  Video, Heart, Eye, Share2, Users, MessageCircle, Bookmark,
  Play, ChevronRight, Upload, Scissors, Wand2, TrendingUp,
  DollarSign, BarChart3, Grid3X3, List, Star, MapPin,
  Calendar, ExternalLink, Mail, Handshake, Settings,
  UserPlus, UserMinus, Loader2, Film, FileText, Sparkles
} from 'lucide-react';
import { TopTippersLeaderboard, TopFansLeaderboard } from './LeaderboardWidgets';

const CATEGORY_COLORS = {
  Comedy: '#F59E0B', Lifestyle: '#EC4899', Skits: '#8B5CF6', Vlogs: '#06B6D4',
  Educational: '#10B981', Fashion: '#F43F5E', Promo: '#F97316', Entertainment: '#6366F1',
  Music: '#A855F7', Dance: '#EF4444', Food: '#84CC16', Tech: '#3B82F6',
  Fitness: '#14B8A6', Beauty: '#E879F9', Gaming: '#22D3EE',
};

function formatCount(n) {
  if (!n) return '0';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function ContentCreatorProfileView({
  profile, isOwnProfile, isFollowing: initialFollowing, onFollow,
  followerCount, followingCount, onShowFollowers, onShowFollowing
}) {
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  const [creatorData, setCreatorData] = useState(null);
  const [contentTab, setContentTab] = useState('videos');
  const [content, setContent] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [viewMode, setViewMode] = useState('grid');
  const [isFollowing, setIsFollowing] = useState(initialFollowing);
  const [localFollowerCount, setLocalFollowerCount] = useState(followerCount);
  const headers = getAuthHeaders();

  useEffect(() => {
    if (profile?.user_id) {
      fetchCreatorData();
      fetchContent('video');
    }
  }, [profile?.user_id]);

  const fetchCreatorData = async () => {
    try {
      const res = await fetch(`${API}/content-creator/profile/${profile.user_id}`);
      if (res.ok) setCreatorData(await res.json());
    } catch (e) { console.error('Creator data fetch error:', e); }
  };

  const fetchContent = async (type) => {
    setContentLoading(true);
    try {
      const sortBy = type === 'trending' ? 'trending' : (type === 'popular' ? 'popular' : 'recent');
      const contentType = type === 'trending' || type === 'popular' ? 'all' : type;
      const res = await fetch(`${API}/content-creator/content/${profile.user_id}?content_type=${contentType}&sort_by=${sortBy}&limit=24`);
      if (res.ok) {
        const data = await res.json();
        setContent(data.content || []);
      }
    } catch (e) { console.error('Content fetch error:', e); }
    finally { setContentLoading(false); }
  };

  const handleTabChange = (tab) => {
    setContentTab(tab);
    const typeMap = { videos: 'video', all: 'all', images: 'image', trending: 'trending', popular: 'popular' };
    fetchContent(typeMap[tab] || 'all');
  };

  const handleFollow = async () => {
    if (onFollow) {
      await onFollow();
      setIsFollowing(!isFollowing);
      setLocalFollowerCount(prev => isFollowing ? prev - 1 : prev + 1);
    }
  };

  const stats = creatorData?.stats || {};
  const settings = creatorData?.settings || {};
  const categoryTags = settings.category_tags || [];
  const pinnedContent = creatorData?.pinned_content || [];
  const featuredVideos = creatorData?.featured_videos || [];

  const bannerUrl = profile.banner_url
    ? (profile.banner_url.startsWith('http') ? profile.banner_url : `${API.replace('/api', '')}${profile.banner_url}`)
    : null;

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="content-creator-profile">
      {/* ──── Banner ──── */}
      <div className="relative h-48 sm:h-64 lg:h-72 overflow-hidden" data-testid="creator-banner">
        {bannerUrl ? (
          <img src={bannerUrl} alt="Banner" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-violet-900/40 via-[#121217] to-cyan-900/30" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
      </div>

      {/* ──── Profile Header ──── */}
      <div className="max-w-6xl mx-auto px-4 -mt-20 relative z-10">
        <div className="flex flex-col sm:flex-row items-start sm:items-end gap-4">
          {/* Avatar */}
          <div className="shrink-0" data-testid="creator-avatar">
            <ClickableProfilePhoto
              src={profile.avatar_url || profile.picture}
              alt={profile.name}
              size={120}
              className="rounded-full border-4 border-[#0B0B0E] shadow-xl"
            />
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 pb-1">
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-2xl sm:text-3xl font-bold truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="creator-name">
                {profile.stage_name || profile.name}
              </h1>
              {profile.is_verified && <Sparkles className="w-5 h-5 text-cyan-400" />}
              <Badge className="bg-gradient-to-r from-violet-600 to-purple-600 text-foreground text-xs px-2.5 py-0.5 font-medium" data-testid="creator-role-badge">
                Content Creator
              </Badge>
            </div>
            <p className="text-muted-foreground text-sm mt-0.5">{profile.handle ? `@${profile.handle}` : ""}</p>
            {profile.bio && <p className="text-foreground/80 text-sm mt-2 max-w-xl line-clamp-2">{profile.bio}</p>}

            {/* Category Tags */}
            {categoryTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="creator-category-tags">
                {categoryTags.map(tag => (
                  <span key={tag} className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ backgroundColor: `${CATEGORY_COLORS[tag] || '#6B7280'}20`, color: CATEGORY_COLORS[tag] || '#9CA3AF' }}>
                    {tag}
                  </span>
                ))}
              </div>
            )}

            {/* Location */}
            {(profile.neighborhood || profile.location) && (
              <p className="text-muted-foreground text-xs mt-1.5 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {profile.neighborhood || profile.location}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 shrink-0 pb-1" data-testid="creator-actions">
            {isOwnProfile ? (
              <>
                <Button size="sm" variant="outline" onClick={() => navigate('/settings')} className="border-border text-foreground/80" data-testid="edit-profile-btn">
                  <Settings className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button size="sm" onClick={() => navigate('/creator/earnings')} className="bg-violet-600 hover:bg-violet-700" data-testid="view-analytics-btn">
                  <BarChart3 className="w-4 h-4 mr-1" /> Analytics
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" onClick={handleFollow}
                  className={isFollowing ? 'bg-muted/50 hover:bg-red-500/20 text-white' : 'bg-violet-600 hover:bg-violet-700'}
                  data-testid="follow-btn">
                  {isFollowing ? <><UserMinus className="w-4 h-4 mr-1" /> Following</> : <><UserPlus className="w-4 h-4 mr-1" /> Follow</>}
                </Button>
                <MessageButton userId={profile.user_id} size="sm" />
                <Button size="sm" variant="outline" onClick={() => navigate(`/hire-board?creator=${profile.user_id}`)} className="border-border text-foreground/80" data-testid="book-creator-btn">
                  <Handshake className="w-4 h-4 mr-1" /> Book
                </Button>
                <Button size="sm" variant="outline" className="border-border text-foreground/80" data-testid="tip-creator-btn"
                  onClick={() => navigate(`/feed`)}>
                  <DollarSign className="w-4 h-4 mr-1" /> Tip
                </Button>
              </>
            )}
          </div>
        </div>

        {/* ──── Stats Row ──── */}
        <div className="flex items-center gap-6 mt-5 flex-wrap" data-testid="creator-stats-row">
          <StatPill icon={Eye} value={formatCount(stats.total_views)} label="views" />
          <StatPill icon={Users} value={formatCount(localFollowerCount)} label="followers" onClick={onShowFollowers} />
          <StatPill icon={Heart} value={formatCount(stats.total_likes)} label="likes" />
          <StatPill icon={Share2} value={formatCount(stats.total_shares)} label="shares" />
          <StatPill icon={Video} value={formatCount(stats.post_count)} label="posts" />
          {stats.engagement_rate > 0 && (
            <span className="text-xs text-emerald-400 font-medium bg-emerald-500/10 px-2 py-1 rounded-full">
              {stats.engagement_rate}% engagement
            </span>
          )}
        </div>
      </div>

      {/* ──── Featured Videos ──── */}
      {featuredVideos.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 mt-8" data-testid="featured-videos-section">
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Star className="w-4 h-4 text-amber-400" /> Featured Videos
          </h2>
          <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
            {featuredVideos.slice(0, 6).map(v => (
              <VideoCard key={v.post_id} post={v} onClick={() => navigate(`/post/${v.post_id}`)} />
            ))}
          </div>
        </div>
      )}

      {/* ──── Pinned Content ──── */}
      {pinnedContent.length > 0 && (
        <div className="max-w-6xl mx-auto px-4 mt-6" data-testid="pinned-content-section">
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Bookmark className="w-4 h-4 text-violet-400" /> Pinned
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {pinnedContent.slice(0, 3).map(p => (
              <Card key={p.post_id} className="bg-card border-border p-3 cursor-pointer hover:border-border transition-colors"
                onClick={() => navigate(`/post/${p.post_id}`)}>
                <p className="text-sm line-clamp-2">{p.content_text || 'Untitled'}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{formatCount(p.views_count)}</span>
                  <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{formatCount(p.likes_count)}</span>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* ──── Creator Tools (own profile) ──── */}
      {isOwnProfile && (
        <div className="max-w-6xl mx-auto px-4 mt-6" data-testid="creator-tools-section">
          <h2 className="text-base font-semibold mb-3 flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-cyan-400" /> Creator Tools
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            <ToolButton icon={Upload} label="Upload" onClick={() => navigate('/post/create')} color="#8B5CF6" />
            <ToolButton icon={Scissors} label="Create Clip" onClick={() => navigate('/creator-editor')} color="#06B6D4" />
            <ToolButton icon={Wand2} label="AI Captions" onClick={() => navigate('/create')} color="#EC4899" />
            <ToolButton icon={TrendingUp} label="Viral Score" onClick={() => navigate('/viral-engine')} color="#10B981" />
            <ToolButton icon={DollarSign} label="Monetize" onClick={() => navigate('/creator/earnings')} color="#F59E0B" />
            <ToolButton icon={BarChart3} label="Analytics" onClick={() => navigate('/viral-engine')} color="#3B82F6" />
          </div>
        </div>
      )}

      {/* ──── Booking Info ──── */}
      {(settings.booking_enabled || settings.business_email || settings.collaboration_preferences) && (
        <div className="max-w-6xl mx-auto px-4 mt-6" data-testid="booking-info-section">
          <Card className="bg-card border-border p-4">
            <h3 className="text-sm font-semibold mb-2 flex items-center gap-2">
              <Handshake className="w-4 h-4 text-amber-400" /> Booking & Collabs
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
              {settings.business_email && (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Mail className="w-4 h-4" /> <a href={`mailto:${settings.business_email}`} className="text-violet-400 hover:underline">{settings.business_email}</a>
                </div>
              )}
              {settings.collaboration_preferences && (
                <p className="text-muted-foreground">{settings.collaboration_preferences}</p>
              )}
              {settings.booking_info && (
                <p className="text-muted-foreground">{settings.booking_info}</p>
              )}
            </div>
          </Card>
        </div>
      )}

      {/* ──── Leaderboards ──── */}
      <div className="max-w-6xl mx-auto px-4 mt-6" data-testid="creator-leaderboards">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <TopTippersLeaderboard creatorUserId={profile.user_id} />
          <TopFansLeaderboard creatorUserId={profile.user_id} />
        </div>
      </div>

      {/* ──── Content Tabs ──── */}
      <div className="max-w-6xl mx-auto px-4 mt-8 pb-20" data-testid="content-tabs-section">
        <div className="flex items-center justify-between mb-4">
          <div className="flex gap-1 overflow-x-auto scrollbar-hide">
            {[
              { id: 'videos', label: 'Videos', icon: Video },
              { id: 'all', label: 'All', icon: Grid3X3 },
              { id: 'images', label: 'Photos', icon: Film },
              { id: 'trending', label: 'Trending', icon: TrendingUp },
            ].map(tab => (
              <button key={tab.id} onClick={() => handleTabChange(tab.id)} data-testid={`content-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${contentTab === tab.id ? 'bg-violet-600 text-white' : 'bg-muted/30 text-muted-foreground hover:text-foreground'}`}>
                <tab.icon className="w-3.5 h-3.5" /> {tab.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            <button onClick={() => setViewMode('grid')} className={`p-1.5 rounded ${viewMode === 'grid' ? 'bg-muted/50 text-white' : 'text-muted-foreground/80'}`}>
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button onClick={() => setViewMode('list')} className={`p-1.5 rounded ${viewMode === 'list' ? 'bg-muted/50 text-white' : 'text-muted-foreground/80'}`}>
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {contentLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
        ) : content.length === 0 ? (
          <div className="text-center py-12">
            <Video className="w-12 h-12 text-muted-foreground/60 mx-auto mb-3" />
            <p className="text-muted-foreground">No content yet</p>
            {isOwnProfile && <Button size="sm" className="mt-3 bg-violet-600" onClick={() => navigate('/post/create')}>Upload First Video</Button>}
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="content-grid">
            {content.map(post => (
              <ContentGridItem key={post.post_id} post={post} onClick={() => navigate(`/post/${post.post_id}`)} />
            ))}
          </div>
        ) : (
          <div className="space-y-2" data-testid="content-list">
            {content.map(post => (
              <ContentListItem key={post.post_id} post={post} onClick={() => navigate(`/post/${post.post_id}`)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* ═══════════ Sub-components ═══════════ */

function StatPill({ icon: Icon, value, label, onClick }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-1.5 text-sm ${onClick ? 'hover:text-foreground cursor-pointer' : 'cursor-default'}`}>
      <Icon className="w-3.5 h-3.5 text-muted-foreground" />
      <span className="font-bold text-white">{value}</span>
      <span className="text-muted-foreground">{label}</span>
    </button>
  );
}

function ToolButton({ icon: Icon, label, onClick, color }) {
  return (
    <button onClick={onClick} className="flex flex-col items-center gap-1.5 p-3 rounded-xl bg-muted/20 hover:bg-muted/30 transition-colors" data-testid={`tool-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="p-2 rounded-lg" style={{ backgroundColor: `${color}15` }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <span className="text-xs text-muted-foreground">{label}</span>
    </button>
  );
}

function VideoCard({ post, onClick }) {
  const mediaUrl = post.media_url
    ? (post.media_url.startsWith('http') ? post.media_url : `${window.location.origin}${post.media_url}`)
    : null;

  return (
    <div onClick={onClick} className="shrink-0 w-44 cursor-pointer group" data-testid={`featured-video-${post.post_id}`}>
      <div className="relative aspect-[9/16] rounded-lg overflow-hidden bg-card">
        {mediaUrl ? (
          <video src={mediaUrl} className="w-full h-full object-cover" muted preload="metadata" />
        ) : (
          <div className="w-full h-full flex items-center justify-center"><Video className="w-8 h-8 text-muted-foreground/60" /></div>
        )}
        <div className="absolute inset-0 bg-black/20 group-hover:bg-black/10 transition-colors flex items-center justify-center">
          <Play className="w-8 h-8 text-foreground/80 drop-shadow-lg" />
        </div>
        <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/80">
          <div className="flex items-center gap-2 text-xs text-foreground/80">
            <span className="flex items-center gap-0.5"><Eye className="w-3 h-3" />{formatCount(post.views_count)}</span>
            <span className="flex items-center gap-0.5"><Heart className="w-3 h-3" />{formatCount(post.likes_count)}</span>
          </div>
        </div>
      </div>
      <p className="text-xs text-muted-foreground mt-1.5 line-clamp-1">{post.content_text || 'Video'}</p>
    </div>
  );
}

function ContentGridItem({ post, onClick }) {
  const isVideo = ['video', 'reel'].includes(post.media_type);
  const mediaUrl = post.media_url
    ? (post.media_url.startsWith('http') ? post.media_url : `${window.location.origin}${post.media_url}`)
    : null;

  return (
    <div onClick={onClick} className="relative aspect-square rounded-lg overflow-hidden bg-card cursor-pointer group" data-testid={`content-grid-${post.post_id}`}>
      {mediaUrl && isVideo ? (
        <video src={mediaUrl} className="w-full h-full object-cover" muted preload="metadata" />
      ) : mediaUrl && post.media_type === 'image' ? (
        <img src={mediaUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center p-3 bg-card">
          <p className="text-xs text-muted-foreground line-clamp-4">{post.content_text || ''}</p>
        </div>
      )}
      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
        <div className="flex items-center gap-3 text-sm font-medium text-white">
          <span className="flex items-center gap-1"><Heart className="w-4 h-4" />{formatCount(post.likes_count)}</span>
          <span className="flex items-center gap-1"><Eye className="w-4 h-4" />{formatCount(post.views_count)}</span>
        </div>
      </div>
      {isVideo && (
        <div className="absolute top-2 right-2">
          <Play className="w-4 h-4 text-white drop-shadow-lg" />
        </div>
      )}
    </div>
  );
}

function ContentListItem({ post, onClick }) {
  const mediaUrl = post.media_url
    ? (post.media_url.startsWith('http') ? post.media_url : `${window.location.origin}${post.media_url}`)
    : null;

  return (
    <Card onClick={onClick} className="flex items-center gap-3 p-3 bg-card border-border cursor-pointer hover:border-border transition-colors" data-testid={`content-list-${post.post_id}`}>
      <div className="w-16 h-16 shrink-0 rounded-lg overflow-hidden bg-card">
        {mediaUrl && post.media_type === 'image' ? (
          <img src={mediaUrl} alt="" className="w-full h-full object-cover" />
        ) : mediaUrl && ['video', 'reel'].includes(post.media_type) ? (
          <div className="w-full h-full flex items-center justify-center bg-muted"><Play className="w-5 h-5 text-muted-foreground" /></div>
        ) : (
          <div className="w-full h-full flex items-center justify-center"><FileText className="w-5 h-5 text-muted-foreground/80" /></div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{post.content_text || 'Untitled'}</p>
        <div className="flex items-center gap-3 mt-1 text-xs text-muted-foreground">
          <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{formatCount(post.views_count)}</span>
          <span className="flex items-center gap-1"><Heart className="w-3 h-3" />{formatCount(post.likes_count)}</span>
          <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{formatCount(post.comments_count)}</span>
          {post.created_at && <span>{new Date(post.created_at).toLocaleDateString()}</span>}
        </div>
      </div>
      <ChevronRight className="w-4 h-4 text-muted-foreground/80 shrink-0" />
    </Card>
  );
}
